import { Copy, Download, Edit, Trash } from 'ck-assets';

import { OurTooltip } from 'ck-components';
import { ReactSVG } from 'react-svg';
import clsx from 'clsx';
import { useId } from 'react';

export interface RowComponentActionProps {
  onRowClick?: (data: any) => void;
  onDeleteClick?: (data: any) => void;
  onEditClick?: (data: any) => void;
  onCopyClick?: (data: any) => void;
  conditionDisableEditClick?: (data: any) => boolean;
  conditionDisableCopyClick?: (data: any) => boolean;
  onDownloadClick?: (data: any) => void;
  onCustomClick?: (data: any) => void;
  customLastColumn?: React.ReactNode;
}

interface RowComponentProps<T> extends RowComponentActionProps {
  dataPaths: Array<keyof T>;
  data: { [key in keyof T]: any };
  /** Column heading labels — required when mode='card' to show labels beside each value */
  colHeadings?: string[];
  /** 'row' (default) renders a <tr> for the desktop table; 'card' renders a mobile card <div> */
  mode?: 'row' | 'card';
}

/**
 * The row component which is rendered within the table.
 *
 * @param dataPaths an array of strings containing the paths which the data will be mapped against
 * @param data the data which will be rendered on the current row
 * @param onRowClick a callback executed whenever the current row is Clicked
 * @param conditionDisableCopyClick condition to hide copy-button depending on row data
 * @param conditionDisableEditClick condition to hide edit-button depending on row data
 */

export default function RowComponent<T extends { [key: string]: any }>({
  dataPaths,
  data,
  onRowClick,
  onDeleteClick,
  onEditClick,
  onCopyClick,
  conditionDisableEditClick,
  conditionDisableCopyClick,
  onCustomClick,
  onDownloadClick,
  customLastColumn,
  colHeadings,
  mode = 'row',
}: RowComponentProps<T>) {
  // Unique prefix per component instance — prevents duplicate tooltip anchor IDs
  // across all rendered rows (and between the mobile card tree and desktop table tree).
  const uid = useId();

  const hasFunctions = onDeleteClick || onEditClick || onCopyClick;

  const enableEdit =
    !conditionDisableEditClick || !conditionDisableEditClick(data);

  const enableCopy =
    !conditionDisableCopyClick || !conditionDisableCopyClick(data);

  // Shared action buttons for both card and row modes.
  // e.stopPropagation() on every button prevents card's onRowClick from firing
  // when the user taps an action button.
  const actionButtons: JSX.Element | null = hasFunctions ? (
    <div className='flex flex-row items-center gap-1'>
      {enableCopy && onCopyClick && (
        <OurTooltip content='kopiera' id={`${uid}-copy`}>
          <button
            aria-label='copyRowButton'
            onClick={(e) => {
              e.stopPropagation();
              onCopyClick(data);
            }}
          >
            <ReactSVG src={Copy} className='w-5 cursor-pointer' />
          </button>
        </OurTooltip>
      )}
      {enableEdit && onEditClick && (
        <OurTooltip content='redigera' id={`${uid}-edit`}>
          <button
            aria-label='editRowButton'
            onClick={(e) => {
              e.stopPropagation();
              onEditClick(data);
            }}
          >
            <ReactSVG src={Edit} className='w-5 cursor-pointer' />
          </button>
        </OurTooltip>
      )}
      {onDownloadClick && (
        <OurTooltip content='ladda ner' id={`${uid}-download`}>
          <button
            aria-label='downloadRowButton'
            onClick={(e) => {
              e.stopPropagation();
              onDownloadClick(data);
            }}
          >
            <ReactSVG
              title='Download button'
              src={Download}
              className='w-5 cursor-pointer'
            />
          </button>
        </OurTooltip>
      )}
      {onDeleteClick && (
        <OurTooltip content='radera' id={`${uid}-delete`}>
          <button
            aria-label='deleteRowButton'
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick(data);
            }}
          >
            <ReactSVG
              title='Delete button'
              src={Trash}
              className='w-5 cursor-pointer'
            />
          </button>
        </OurTooltip>
      )}
    </div>
  ) : null;

  // Card layout for mobile
  if (mode === 'card') {
    return (
      <div
        className={clsx(
          'rounded-2xl border border-neutral-200 bg-white px-5 py-3 shadow-sm',
          onRowClick && 'cursor-pointer'
        )}
        onClick={() => onRowClick && onRowClick(data)}
      >
        {dataPaths.map((path, index) => (
          <div
            key={`${String(path)}-${index}`}
            className='flex items-start justify-between gap-2 border-b border-gray-100 py-2 last:border-none'
          >
            <span className='shrink-0 text-xs font-normal uppercase tracking-wide text-gray-500'>
              {colHeadings?.[index] ?? String(path)}
            </span>
            <span className='text-right text-sm font-light text-gray-800'>
              {data[path]}
            </span>
          </div>
        ))}
        {(actionButtons || customLastColumn) && (
          <div className='mt-2 flex items-center justify-end gap-2 pt-1'>
            {actionButtons}
            {customLastColumn && (
              <div
                onClick={
                  onCustomClick
                    ? (e) => {
                        e.stopPropagation();
                        onCustomClick(data);
                      }
                    : undefined
                }
              >
                {customLastColumn}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Default table row
  return (
    <tr className='w-full'>
      {dataPaths.map((path, index) => (
        <td
          key={`${data[path]}-${index}`}
          className={clsx(
            'h-16 overflow-hidden text-ellipsis whitespace-nowrap border-t border-b bg-white px-4 py-4 font-light first:rounded-l-2xl first:border-l first:pl-7 last:rounded-r-2xl last:border-r last:pr-7',
            onRowClick && 'hover:cursor-pointer'
          )}
          onClick={() => onRowClick && onRowClick(data)}
        >
          <OurTooltip content={data[path]} id={data[path]}>
            {data[path]}
          </OurTooltip>
        </td>
      ))}
      {hasFunctions && (
        <td
          key={'functions'}
          className=' h-16 gap-2 place-self-end overflow-hidden text-ellipsis whitespace-nowrap border-t border-b bg-white px-4 py-4 font-light last:rounded-r-2xl last:border-r last:pr-7'
        >
          <div className='flex flex-row items-center justify-end gap-1'>
            {actionButtons}
          </div>
        </td>
      )}
      {customLastColumn && (
        <td
          onClick={onCustomClick ? () => onCustomClick(data) : undefined}
          className={
            'flex h-16 flex-row items-center justify-end gap-2 place-self-end text-ellipsis whitespace-nowrap border-t border-b bg-white px-4 py-4 font-light first:border-l last:rounded-r-2xl last:border-r last:pr-7'
          }
        >
          {customLastColumn}
        </td>
      )}
    </tr>
  );
}
